import React from 'react';
import { ArrowUp, ArrowDown, ArrowLeft, ArrowRight } from 'lucide-react';
import roadcastIcon from '../assets/roadcast-icon.png';

/**
 * Roadcast × Alcochek Drive shared UI atoms.
 * These are pure presentational primitives — they never talk to game state.
 */

const ROADCAST_URL = 'https://roadcast.in';

const linkResetStyle = {
  textDecoration: 'none',
  color: 'inherit',
  display: 'inline-flex',
  alignItems: 'center',
  cursor: 'pointer'
};

export const RoadcastLogo = ({ variant = 'lockup', height = 32, style, testId }) => {
  const commonLinkProps = {
    href: ROADCAST_URL,
    target: '_blank',
    rel: 'noopener noreferrer',
    title: 'Visit roadcast.in',
    'aria-label': 'Visit roadcast.in (opens in a new tab)',
    'data-testid': testId || (variant === 'icon' ? 'roadcast-logo-icon' : 'roadcast-logo')
  };

  // Icon-only variant.
  if (variant === 'icon') {
    return (
      <a {...commonLinkProps} style={{ ...linkResetStyle, ...style }}>
        <img
          src={roadcastIcon}
          alt="RoadCast"
          draggable={false}
          style={{
            height,
            width: 'auto',
            display: 'block',
            userSelect: 'none',
            WebkitUserSelect: 'none',
            borderRadius: Math.round(height * 0.18)
          }}
        />
      </a>
    );
  }

  // Lockup: icon + wordmark. We compose the wordmark in HTML so the colour
  // matches the surrounding theme (the shipped lockup PNG has a white bg).
  const wordSize = Math.round(height * 0.6);
  return (
    <a
      {...commonLinkProps}
      style={{
        ...linkResetStyle,
        gap: Math.round(height * 0.28),
        ...style
      }}
    >
      <img
        src={roadcastIcon}
        alt=""
        draggable={false}
        style={{
          height,
          width: height,
          display: 'block',
          borderRadius: Math.round(height * 0.18),
          userSelect: 'none',
          WebkitUserSelect: 'none'
        }}
      />
      <span
        style={{
          fontFamily: 'var(--rc-font-display)',
          fontWeight: 800,
          fontSize: wordSize,
          letterSpacing: '-0.01em',
          color: 'var(--rc-text-primary)',
          lineHeight: 1
        }}
      >
        Road<span style={{ color: 'var(--rc-teal-bright)' }}>Cast</span>
      </span>
    </a>
  );
};

export const HudPanel = ({ label, children, style, testId }) => (
  <div
    className="rc-panel rc-enter"
    data-testid={testId}
    style={{
      padding: '14px 16px',
      minWidth: 168,
      fontFamily: 'var(--rc-font-display)',
      ...style
    }}
  >
    <div className="rc-tag" style={{ marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
      <span
        style={{
          width: 6,
          height: 6,
          borderRadius: '50%',
          background: 'var(--rc-teal)',
          boxShadow: '0 0 6px var(--rc-teal)'
        }}
      />
      {label}
    </div>
    {children}
  </div>
);

export const ConditionCard = ({
  active,
  onClick,
  tone = 'teal', // 'teal' | 'amber' | 'danger'
  title,
  description,
  icon,
  testId
}) => {
  const toneColor =
    tone === 'amber' ? 'var(--rc-amber)' :
    tone === 'danger' ? 'var(--rc-danger)' :
    'var(--rc-teal)';

  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      data-active={active ? 'true' : 'false'}
      data-tone={tone}
      className="rc-condition-card"
      style={{ color: 'var(--rc-text-primary)', textAlign: 'center' }}
    >
      <div
        className="rc-card-icon-wrap"
        style={{
          width: 66,
          height: 66,
          borderRadius: '50%',
          margin: '0 auto 14px',
          border: `1.5px solid ${toneColor}`,
          background: 'rgba(0,0,0,0.55)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: toneColor,
          boxShadow: `0 0 18px ${toneColor}33, inset 0 0 12px ${toneColor}22`
        }}
      >
        {icon}
      </div>
      <div
        className="rc-card-title"
        style={{
          fontFamily: 'var(--rc-font-display)',
          fontWeight: 900,
          fontSize: 22,
          letterSpacing: '0.06em',
          marginBottom: 8
        }}
      >
        {title}
      </div>
      <div
        className="rc-card-desc"
        style={{
          fontSize: 12,
          color: 'var(--rc-text-secondary)',
          letterSpacing: '0.02em',
          marginBottom: 14
        }}
      >
        {description}
      </div>
      <div
        aria-hidden
        style={{
          width: 42,
          height: 2,
          borderRadius: 2,
          background: toneColor,
          opacity: active ? 1 : 0.35,
          margin: '0 auto 10px',
          transition: 'opacity 220ms ease'
        }}
      />
      <div
        aria-hidden
        style={{
          width: 12,
          height: 12,
          borderRadius: '50%',
          border: `1.5px solid ${toneColor}`,
          margin: '0 auto',
          background: active ? toneColor : 'transparent',
          boxShadow: active ? `0 0 12px ${toneColor}` : 'none',
          transition: 'background 200ms ease, box-shadow 200ms ease'
        }}
      />
    </button>
  );
};

export const GameButton = ({
  variant = 'primary', // 'primary' | 'secondary'
  onClick,
  disabled,
  icon,
  children,
  testId,
  style
}) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    data-testid={testId}
    className={variant === 'primary' ? 'rc-primary-btn' : 'rc-secondary-btn'}
    style={style}
  >
    {icon && (
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 26,
          height: 26,
          borderRadius: '50%',
          border: '1px solid currentColor',
          opacity: 0.9
        }}
      >
        {icon}
      </span>
    )}
    <span>{children}</span>
  </button>
);

export const StatCard = ({ label, value, icon, testId }) => (
  <div
    className="rc-panel rc-stat-card"
    data-testid={testId}
    style={{ padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 12 }}
  >
    <div
      className="rc-stat-card-icon"
      style={{
        width: 38,
        height: 38,
        minWidth: 38,
        borderRadius: 10,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'var(--rc-teal)',
        border: '1px solid var(--rc-border-subtle)',
        background: 'rgba(6, 16, 18, 0.65)'
      }}
    >
      {icon}
    </div>
    <div style={{ minWidth: 0 }}>
      <div className="rc-tag rc-stat-card-label" style={{ marginBottom: 2 }}>
        {label}
      </div>
      <div
        className="rc-stat-card-value"
        style={{
          fontFamily: 'var(--rc-font-display)',
          fontWeight: 800,
          fontSize: 20,
          color: 'var(--rc-text-primary)',
          lineHeight: 1.1,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis'
        }}
      >
        {value}
      </div>
    </div>
  </div>
);

/**
 * Segmented risk meter — teal → green → amber → orange → red.
 * `percent` is 0-1; renders a marker at that position.
 */
export const ImpairmentMeter = ({ percent, riskLabel, bacText }) => {
  const clamped = Math.max(0, Math.min(1, percent || 0));
  const segments = 14;
  const activeIdx = Math.round(clamped * (segments - 1));

  const colorFor = (i) => {
    const t = i / (segments - 1);
    if (t < 0.25) return 'rgba(34, 211, 195, 0.85)';
    if (t < 0.45) return 'rgba(120, 220, 140, 0.85)';
    if (t < 0.65) return 'rgba(246, 183, 86, 0.9)';
    if (t < 0.82) return 'rgba(255, 140, 90, 0.9)';
    return 'rgba(255, 91, 106, 0.95)';
  };

  return (
    <div
      className="rc-panel"
      data-testid="impairment-meter"
      style={{
        padding: '18px 22px',
        display: 'grid',
        gridTemplateColumns: '1fr 2fr auto',
        alignItems: 'center',
        gap: 20
      }}
    >
      <div>
        <div className="rc-tag" style={{ marginBottom: 4 }}>
          Impairment Level
        </div>
        <div
          style={{
            fontFamily: 'var(--rc-font-display)',
            fontWeight: 800,
            fontSize: 16,
            color: percent > 0.6 ? 'var(--rc-danger)' : percent > 0.3 ? 'var(--rc-amber)' : 'var(--rc-teal)',
            letterSpacing: '0.04em'
          }}
        >
          {riskLabel}
        </div>
      </div>

      <div style={{ position: 'relative' }}>
        <div style={{ display: 'flex', gap: 3, alignItems: 'stretch' }}>
          {Array.from({ length: segments }).map((_, i) => (
            <div
              key={i}
              style={{
                flex: 1,
                height: 14,
                borderRadius: 2,
                background: colorFor(i),
                opacity: i <= activeIdx ? 1 : 0.18,
                transition: 'opacity 400ms ease'
              }}
            />
          ))}
        </div>
        <div
          aria-hidden
          style={{
            position: 'absolute',
            top: -4,
            bottom: -4,
            width: 3,
            borderRadius: 2,
            background: '#ffffff',
            left: `calc(${clamped * 100}% - 1px)`,
            boxShadow: '0 0 10px rgba(255,255,255,0.6)',
            transition: 'left 500ms cubic-bezier(0.2,0.8,0.2,1)'
          }}
        />
      </div>

      <div style={{ textAlign: 'right' }}>
        <div
          style={{
            fontFamily: 'var(--rc-font-display)',
            fontWeight: 900,
            fontSize: 22,
            color: percent > 0.6 ? 'var(--rc-danger)' : 'var(--rc-text-primary)'
          }}
        >
          {bacText}
        </div>
        <div className="rc-tag">BAC</div>
      </div>
    </div>
  );
};

export const CampaignBadge = () => (
  <a
    href={ROADCAST_URL}
    target="_blank"
    rel="noopener noreferrer"
    title="Visit roadcast.in"
    aria-label="Control Ka Bhram — a RoadCast initiative (opens roadcast.in in a new tab)"
    data-testid="campaign-badge"
    className="rc-panel"
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 12,
      padding: '10px 20px',
      borderRadius: 999,
      textDecoration: 'none',
      color: 'inherit',
      cursor: 'pointer'
    }}
  >
    <img
      src={roadcastIcon}
      alt=""
      draggable={false}
      style={{ width: 22, height: 22, borderRadius: 5, display: 'block' }}
    />
    <div style={{ lineHeight: 1.1 }}>
      <div
        style={{
          fontFamily: 'var(--rc-font-display)',
          fontWeight: 700,
          fontSize: 15,
          color: 'var(--rc-text-primary)',
          letterSpacing: '0.02em'
        }}
      >
        Control Ka Bhram
      </div>
      <div
        style={{
          fontSize: 10,
          letterSpacing: '0.2em',
          color: 'var(--rc-text-muted)',
          fontFamily: 'var(--rc-font-mono)',
          textTransform: 'uppercase',
          marginTop: 2
        }}
      >
        A RoadCast Initiative
      </div>
    </div>
  </a>
);

const ArrowKey = ({ children }) => (
  <span
    aria-hidden
    style={{
      width: 26,
      height: 26,
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      border: '1px solid var(--rc-border-subtle)',
      borderRadius: 6,
      color: 'var(--rc-text-secondary)',
      background: 'rgba(8, 20, 24, 0.6)'
    }}
  >
    {children}
  </span>
);

export const ArrowKeysHint = () => (
  <div
    style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 12,
      color: 'var(--rc-text-secondary)',
      fontFamily: 'var(--rc-font-display)',
      fontSize: 14
    }}
  >
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
      <ArrowKey><ArrowUp size={14} /></ArrowKey>
      <div style={{ display: 'flex', gap: 3 }}>
        <ArrowKey><ArrowLeft size={14} /></ArrowKey>
        <ArrowKey><ArrowDown size={14} /></ArrowKey>
        <ArrowKey><ArrowRight size={14} /></ArrowKey>
      </div>
    </div>
    <span>Use arrow keys to control the vehicle.</span>
  </div>
);
